// "Company Legal Name" in the agreement terms, the disclosures and the E-SIGN
// consent names the party the customer contracts with, so it has to be the
// company's legal name — the one the signed PDF prints. The portal learns it
// from two responses that load at different times, or not at all for a guest:
//
// - get-markers (signed-in customers) sends it as "Company: Legal Name";
// - get-portal-company-markers sends it as "Company Legal Name".
//
// The agreement, payment link and invoice responses only carry the display
// name, which is the last resort. Every handler that touches those markers
// re-applies this, so the legal name wins whichever response lands last.

type Markers = { [key: string]: any } | null | undefined;

// Stored values can carry stray whitespace ("Fintrix LLC ").
const clean = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

export const resolveCompanyLegalName = ({
  paymentMarkers,
  companyMarkers,
  fallbackName,
}: {
  paymentMarkers?: Markers;
  companyMarkers?: Markers;
  fallbackName?: unknown;
}): string =>
  clean(paymentMarkers?.["Company: Legal Name"]) ||
  clean(companyMarkers?.["Company Legal Name"]) ||
  clean(fallbackName);

// The payment markers with "Company Legal Name" set to the resolved name. The
// exact key wins over "Company: Legal Name" when markers are replaced.
export const withCompanyLegalName = ({
  paymentMarkers,
  companyMarkers,
  fallbackName,
}: {
  paymentMarkers?: Markers;
  companyMarkers?: Markers;
  fallbackName?: unknown;
}): { [key: string]: any } => ({
  ...(paymentMarkers || {}),
  "Company Legal Name": resolveCompanyLegalName({
    paymentMarkers,
    companyMarkers,
    fallbackName,
  }),
});
